import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useAddResellerCustomer } from '@/api/resellers';
import { CustomerSearchSelect } from '@/pages/contracts/new/customer-search-select';

interface AddResellerCustomerDialogProps {
  open: boolean;
  resellerId: number;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Dialog to link an existing customer to a reseller. Uses the shared
 * `CustomerSearchSelect` Combobox (async, debounced, same as contract wizard).
 * Write action is coordinator-owned; the user triggers the mutation here.
 */
export function AddResellerCustomerDialog({
  open,
  resellerId,
  onClose,
  onSuccess,
}: AddResellerCustomerDialogProps) {
  const [customerId, setCustomerId] = useState(0);
  const [error, setError] = useState('');

  const add = useAddResellerCustomer(resellerId);

  const handleClose = () => {
    setCustomerId(0);
    setError('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!customerId) return;
    setError('');
    try {
      await add.mutateAsync({ customer_id: customerId, status: 'active' });
      setCustomerId(0);
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add customer.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Customer</DialogTitle>
        </DialogHeader>

        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {error ? <Alert tone="danger">{error}</Alert> : null}

          <CustomerSearchSelect
            value={customerId}
            onChange={setCustomerId}
            label="Customer"
            required
          />
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={handleClose} disabled={add.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={add.isPending}
            disabled={!customerId || add.isPending}
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
